import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Share, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'refdash-install-dismissed';
const DISMISS_DURATION_MS = 3 * 24 * 60 * 60 * 1000; // re-show after 3 days

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator &&
      (navigator as { standalone?: boolean }).standalone === true)
  );
}

function isIOS() {
  return /iPhone|iPad/.test(navigator.userAgent);
}

function wasDismissedRecently(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  return Date.now() - ts < DISMISS_DURATION_MS;
}

export function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Already installed or recently dismissed
    if (isStandalone() || wasDismissedRecently()) return;

    if (isIOS()) {
      // iOS: no native prompt, show guide after a short delay
      const t = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(t);
    }

    // Android/Chrome: capture the beforeinstallprompt event
    function handler(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    }
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIOSGuide(false);
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setVisible(false);
      }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      setShowIOSGuide(true);
    }
  }, [deferredPrompt]);

  // Hide once app enters standalone mode
  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    const handler = () => {
      if (mq.matches) setVisible(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className="fixed bottom-20 inset-x-0 z-[60] px-4 lg:bottom-4 lg:left-auto lg:right-4 lg:max-w-sm"
        >
          <div className="bg-card rounded-2xl border border-border shadow-lg p-4">
            {!showIOSGuide ? (
              /* ── Default banner ───────────────────── */
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Download className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground">
                    Install RefDash
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Add to your home screen for quick access and push
                    notifications.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" onClick={handleInstall}>
                      {isIOS() ? 'Show me how' : 'Install'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={dismiss}>
                      Not now
                    </Button>
                  </div>
                </div>
                <button
                  onClick={dismiss}
                  className="p-1 -mt-1 -mr-1 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              /* ── iOS step-by-step guide ────────────── */
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-bold text-foreground">
                    Add to Home Screen
                  </p>
                  <button
                    onClick={dismiss}
                    className="p-1 -mt-1 -mr-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <ol className="space-y-3 text-xs text-foreground">
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-[10px] font-bold text-primary">
                      1
                    </span>
                    <span>
                      Tap the{' '}
                      <Share className="inline w-3.5 h-3.5 text-primary -mt-0.5" />{' '}
                      <span className="font-semibold">Share</span> button in
                      Safari's toolbar
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-[10px] font-bold text-primary">
                      2
                    </span>
                    <span>
                      Scroll down and tap{' '}
                      <Plus className="inline w-3.5 h-3.5 text-primary -mt-0.5" />{' '}
                      <span className="font-semibold">Add to Home Screen</span>
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-[10px] font-bold text-primary">
                      3
                    </span>
                    <span>
                      Tap <span className="font-semibold">Add</span> to confirm
                    </span>
                  </li>
                </ol>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={dismiss}
                  className="w-full mt-3"
                >
                  Got it
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
